// Multi-photo upload, browser side. UI-free: the per-file request is injected, so the queue is unit-tested
// without a browser. Files go one after another through the existing single-file endpoint (server contract
// unchanged). The server allows 10 upload requests per minute; on HTTP 429 the queue waits and retries
// the same file instead of dropping it.

export const PHOTO_MAX_BYTES = 10 * 1024 * 1024
export const MAX_429_RETRIES = 3
export const DEFAULT_429_WAIT_SECONDS = 60
const MAX_WAIT_SECONDS = 120

export type FileStatus = "ok" | "failed" | "skipped"

export type UploadResponse = {
  ok: boolean
  status: number
  headers: { get(name: string): string | null }
}

export type FileResult = {
  file: File
  status: FileStatus
  /** German reason for failed / skipped files. */
  reason?: string
  response?: UploadResponse
}

export type QueueProgress = {
  /** 1-based position of the file being handled. */
  current: number
  total: number
  phase: "uploading" | "waiting"
  /** Seconds the queue pauses because of the upload limit (phase "waiting"). */
  waitSeconds?: number
  /** Results of the files finished so far (same order as the input). */
  results: FileResult[]
}

/** Throw from the upload callback to mark a file as skipped (not failed) with a reason. */
export class UploadSkipError extends Error {
  constructor(public readonly reason: string) {
    super(reason)
    this.name = "UploadSkipError"
  }
}

export type QueueOptions = {
  upload: (file: File, signal: AbortSignal) => Promise<UploadResponse>
  onProgress?: (p: QueueProgress) => void
  signal?: AbortSignal
  /** Test seam. Must resolve after `ms`, or reject/resolve early when the signal aborts. */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>
}

/** Splits a picked / dropped selection into uploadable images and skipped files with a German reason. */
export function validateImageFiles(files: ArrayLike<File>): { valid: File[]; skipped: FileResult[] } {
  const valid: File[] = []
  const skipped: FileResult[] = []
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (!file.type.startsWith("image/")) {
      skipped.push({ file, status: "skipped", reason: "Keine Bilddatei" })
    } else if (file.size > PHOTO_MAX_BYTES) {
      skipped.push({ file, status: "skipped", reason: "Größer als 10 MB" })
    } else {
      valid.push(file)
    }
  }
  return { valid, skipped }
}

/** Seconds to wait after a 429: Retry-After (seconds or HTTP date), else RateLimit-Reset, else 60. */
export function waitSecondsFor429(headers: { get(name: string): string | null }): number {
  const retryAfter = headers.get("Retry-After")
  if (retryAfter) {
    const n = Number(retryAfter)
    if (Number.isFinite(n) && n >= 0) return clampWait(n)
    const date = Date.parse(retryAfter)
    if (!Number.isNaN(date)) return clampWait((date - Date.now()) / 1000)
  }
  const reset = headers.get("RateLimit-Reset") ?? headers.get("X-RateLimit-Reset")
  if (reset) {
    const n = Number(reset)
    if (Number.isFinite(n) && n >= 0) return clampWait(n)
  }
  return DEFAULT_429_WAIT_SECONDS
}

function clampWait(seconds: number): number {
  return Math.min(MAX_WAIT_SECONDS, Math.max(1, Math.ceil(seconds)))
}

function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve()
    const onAbort = () => { clearTimeout(timer); resolve() }
    const timer = setTimeout(() => { signal.removeEventListener("abort", onAbort); resolve() }, ms)
    signal.addEventListener("abort", onAbort, { once: true })
  })
}

/** Uploads the files strictly one after another. Never throws; every input file gets a result. */
export async function runUploadQueue(files: File[], opts: QueueOptions): Promise<FileResult[]> {
  const controller = new AbortController()
  const outer = opts.signal
  if (outer) {
    if (outer.aborted) controller.abort()
    else outer.addEventListener("abort", () => controller.abort(), { once: true })
  }
  const signal = controller.signal
  const sleep = opts.sleep ?? defaultSleep
  const results: FileResult[] = []
  const emit = (current: number, phase: QueueProgress["phase"], waitSeconds?: number) =>
    opts.onProgress?.({ current, total: files.length, phase, waitSeconds, results: [...results] })

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (signal.aborted) {
      results.push({ file, status: "skipped", reason: "Abgebrochen" })
      continue
    }
    let retries = 0
    let result: FileResult | null = null
    while (!result) {
      emit(i + 1, "uploading")
      try {
        const response = await opts.upload(file, signal)
        if (response.ok) {
          result = { file, status: "ok", response }
        } else if (response.status === 429) {
          if (retries >= MAX_429_RETRIES) {
            result = { file, status: "failed", reason: "Zu viele Uploads, bitte später erneut versuchen" }
          } else {
            retries++
            const seconds = waitSecondsFor429(response.headers)
            emit(i + 1, "waiting", seconds)
            await sleep(seconds * 1000, signal)
            if (signal.aborted) result = { file, status: "skipped", reason: "Abgebrochen" }
          }
        } else {
          result = { file, status: "failed", reason: "Upload fehlgeschlagen", response }
        }
      } catch (err) {
        if (signal.aborted) result = { file, status: "skipped", reason: "Abgebrochen" }
        else if (err instanceof UploadSkipError) result = { file, status: "skipped", reason: err.reason }
        else result = { file, status: "failed", reason: "Upload fehlgeschlagen" }
      }
    }
    results.push(result)
  }
  emit(files.length, "uploading")
  return results
}

/** "3 Fotos hochgeladen, 1 fehlgeschlagen" (plus ", 2 übersprungen" when files were skipped). */
export function summarizeResults(results: FileResult[]): string {
  const ok = results.filter((r) => r.status === "ok").length
  const failed = results.filter((r) => r.status === "failed").length
  const skipped = results.filter((r) => r.status === "skipped").length
  let text = `${ok} ${ok === 1 ? "Foto" : "Fotos"} hochgeladen, ${failed} fehlgeschlagen`
  if (skipped > 0) text += `, ${skipped} übersprungen`
  return text
}

export function progressLabel(p: QueueProgress): string {
  return `Foto ${p.current} von ${p.total}`
}
